import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../components/ui/Spinner';
import { getProjection } from '../features/classroom/classroomApi';
import type { ProjectionPayload } from '../features/classroom/classroomApi';
import ProjectionOptionsList from '../features/projection/ProjectionOptionsList';
import ProjectionCounter from '../features/projection/ProjectionCounter';
import ProjectionBars from '../features/projection/ProjectionBars';
import { QUESTION_STYLE, SESSION_LABEL_STYLE } from '../features/projection/projectionScale';

// WIP-011 (design §1, §2, §3) — the chrome-less wall: `/project/:sessionId`,
// outside AppShell (routes.tsx), opened from the run-sheet's action bar in a
// named window. It is a SEPARATE document from the run-sheet, so its own 5 s
// poll of ONE aggregated endpoint is not a second polling loop inside any
// page (design §2.1, C9) — nothing in the run-sheet gains a poll for this.
//
// The one rule this whole file exists to enforce, same as the attendance
// code screen it sits beside: a STALE wall is worse than no wall. The instant
// a poll fails, the payload is cleared — never left on screen next to an
// error line (see `poll` below).
const POLL_MS = 5000;

const IDLE_MESSAGE_STYLE = { fontSize: 'max(20px, 4vh)', fontWeight: 500 } as const;

function messageForError(err: unknown): { message: string; fatal: boolean } {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    if (status === 401 || status === 403 || status === 404) {
      return { message: 'Projection lost — check the run-sheet.', fatal: true };
    }
    return { message: 'Projection lost — check the run-sheet.', fatal: false };
  }
  return { message: 'Projection lost — check the run-sheet.', fatal: false };
}

export default function ProjectionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [data, setData] = useState<ProjectionPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    try {
      const payload = await getProjection(sessionId);
      if (!alive.current) return;
      setData(payload);
      setError(null);
      timer.current = setTimeout(poll, POLL_MS);
    } catch (err) {
      if (!alive.current) return;
      // Clear immediately — never leave a stale question or stale bars next
      // to the error line.
      setData(null);
      const { message, fatal } = messageForError(err);
      setError(message);
      if (!fatal) timer.current = setTimeout(poll, POLL_MS);
    }
  }, [sessionId]);

  useEffect(() => {
    alive.current = true;
    void poll();
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [poll]);

  if (!sessionId) return null;

  return (
    <div className="flex min-h-screen w-full flex-col bg-ws-surface p-[3vh] text-ws-ink">
      {data && (
        <header className="flex items-center justify-between">
          <span style={SESSION_LABEL_STYLE} className="text-ws-ink-2">
            {data.session.label}{data.activity ? ` · ${data.activity.title}` : ''}
          </span>
          {data.state === 'shown' && data.step?.revote_of && (
            <span style={SESSION_LABEL_STYLE} className="flex items-center gap-2 text-ws-ink-2">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm border-[2px] border-ws-ink-2" aria-hidden="true" /> Round 1
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm bg-ws-accent" aria-hidden="true" /> Round 2
              </span>
            </span>
          )}
        </header>
      )}

      <main className="flex flex-1 flex-col items-center justify-center gap-[4vh]">
        {!data && !error && <Spinner size={28} label="Loading…" />}

        {error && (
          <p style={IDLE_MESSAGE_STYLE} className="text-ws-ink-2" role="alert">{error}</p>
        )}

        {data && !error && data.session.status !== 'live' && (
          <p style={IDLE_MESSAGE_STYLE} className="text-ws-ink-2">Class has not started.</p>
        )}

        {data && !error && data.session.status === 'live' && data.state === 'idle' && (
          <p style={IDLE_MESSAGE_STYLE} className="text-ws-ink-2">Waiting for the next activity</p>
        )}

        {data && !error && data.session.status === 'live' && data.step && (
          <div className="flex w-full max-w-5xl flex-col items-center gap-[4vh]">
            <h1 style={QUESTION_STYLE} className="w-full text-center text-ws-ink">{data.step.prompt}</h1>

            {data.state === 'open' && (
              <>
                {data.step.options && data.step.options.length > 0 && (
                  <ProjectionOptionsList options={data.step.options} />
                )}
                {data.step.mode !== 'instruct' && data.step.mode !== 'group' && (
                  <ProjectionCounter count={data.count} expected={data.expected} />
                )}
              </>
            )}

            {data.state === 'shown' && (
              data.step.options && data.step.options.length > 0 ? (
                <ProjectionBars
                  step={data.step} choices={data.choices} count={data.count}
                  expected={data.expected} revote={data.revote}
                />
              ) : (
                <p style={IDLE_MESSAGE_STYLE} className="text-ws-ink-2">
                  {data.expected != null ? `${data.count} of ${data.expected} answered` : `${data.count} answered`}
                </p>
              )
            )}
          </div>
        )}
      </main>
    </div>
  );
}
